import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { fetchFloorplans } from '@/api/floorplans.api'
import { fetchDeskNumbers } from '@/api/counters.api'

export interface CounterFilterValues {
    deskNumber?: number
    name: string
    unitId?: string
    unitName?: string
    diagramId?: string
    diagramName?: string
    time?: DateRangeValue
    status?: number
}

interface CounterFilterPanelProps {
    values: CounterFilterValues
    onChange: (values: CounterFilterValues) => void
    onSearch: () => void
    showUnitColumn?: boolean
}

const formatDesk = (val: string | number) => String(val).padStart(2, '0');

export function CounterFilterPanel({ values, onChange, onSearch, showUnitColumn = true }: CounterFilterPanelProps) {
    function set<TKey extends keyof CounterFilterValues>(key: TKey, value: CounterFilterValues[TKey]) {
        onChange({ ...values, [key]: value })
    }

    return (
        <FilterFormWrapper onSearch={onSearch} className="rounded-xl border border-gray-300 bg-white px-6 py-5">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Số quầy</span>
                        <Select
                            lazy
                            allowClear
                            initialPage={0}
                            className="w-full"
                            placeholder="Tất cả"
                            allLabel="Tất cả số quầy"
                            cacheKey="counter-desk"
                            value={values.deskNumber ? formatDesk(values.deskNumber) : undefined}
                            initialOptions={
                                values.deskNumber
                                    ? [{
                                        value: formatDesk(values.deskNumber),
                                        label: formatDesk(values.deskNumber)
                                    }]
                                    : []
                            }
                            loadOptions={async ({ page, signal }) => {
                                const { items = [] } = await fetchDeskNumbers(page, 10, {}, signal)

                                const uniqueDesks = [...new Set(items.map((item: any) => item.deskNumber))]
                                const hasNextPage = items.length >= 10

                                return {
                                    options: uniqueDesks.map(desk => ({
                                        value: formatDesk(desk),
                                        label: formatDesk(desk)
                                    })),
                                    hasNextPage,
                                    nextPage: hasNextPage ? page + 1 : undefined,
                                }
                            }}
                            onLazyValueChange={(deskNum) => {
                                onChange({
                                    ...values,
                                    deskNumber: deskNum ? Number(deskNum) : undefined
                                })
                            }}
                        />
                    </div>
                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Tên quầy</span>
                        <Input
                            placeholder="Nhập tên quầy"
                            value={values.name}
                            onChange={(e: { target: { value: string } }) => set('name', e.target.value)}
                        />
                    </div>

                    {showUnitColumn && (
                        <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                            <span className="text-sm font-medium">Đơn vị</span>
                            <Select
                                initialPage={0}
                                cacheKey="counter-filter-unit-select"
                                lazy
                                placeholder="Tất cả đơn vị"
                                value={values.unitId || undefined}
                                className="w-full"
                                allowClear
                                enableRemoteSearch
                                allLabel="Tất cả đơn vị"
                                initialOptions={
                                    values.unitId && values.unitName
                                        ? [{ value: values.unitId, label: values.unitName }]
                                        : []
                                }
                                loadOptions={async ({ page, signal, searchText }) => {
                                    const res = await fetchUnits(
                                        page,
                                        10,
                                        { keySearch: searchText?.trim() || undefined },
                                        signal
                                    )
                                    const arr = Array.isArray(res.items) ? res.items : []
                                    const hasNextPage = arr.length >= 10
                                    return {
                                        options: arr.map((u: any) => ({ value: u.id, label: u.name })),
                                        hasNextPage,
                                        nextPage: hasNextPage ? page + 1 : undefined,
                                    }
                                }}
                                onLazyValueChange={(unitId, option) => {
                                    onChange({
                                        ...values,
                                        unitId: unitId,
                                        unitName: option?.label ? String(option.label) : undefined,
                                        diagramId: undefined,
                                        diagramName: undefined
                                    })
                                }}
                            />
                        </div>
                    )}

                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Sơ đồ</span>
                        <Select
                            initialPage={0}
                            cacheKey={`diagram-select-counter-filter-${values.unitId ?? 'all'}`}
                            lazy
                            placeholder="Tất cả sơ đồ"
                            value={values.diagramId || undefined}
                            className="w-full"
                            allowClear
                            enableRemoteSearch
                            allLabel="Tất cả sơ đồ"
                            initialOptions={
                                values.diagramId && values.diagramName
                                    ? [{ value: values.diagramId, label: values.diagramName }]
                                    : []
                            }
                            loadOptions={async ({ page, signal, searchText }) => {
                                const res = await fetchFloorplans(
                                    page,
                                    10,
                                    {
                                        keySearch: searchText?.trim() || undefined,
                                        unitId: values.unitId || undefined
                                    },
                                    signal
                                )
                                const arr = Array.isArray(res.items) ? res.items : []
                                const hasNextPage = arr.length >= 10
                                return {
                                    options: arr.map((d: any) => ({ value: d.id, label: d.name })),
                                    hasNextPage,
                                    nextPage: hasNextPage ? page + 1 : undefined,
                                }
                            }}
                            onLazyValueChange={(diagramId, option) => {
                                onChange({
                                    ...values,
                                    diagramId: diagramId,
                                    diagramName: option?.label ? String(option.label) : undefined,
                                })
                            }}
                        />
                    </div>

                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Trạng thái</span>
                        <Select
                            placeholder="Tất cả"
                            value={values.status}
                            className="w-full"
                            onValueChange={(v) => set('status', v)}
                            allowClear
                            options={[
                                { value: 1, label: 'Hoạt động' },
                                { value: 0, label: 'Không hoạt động' }
                            ]}
                        />
                    </div>
                    <div className="flex min-w-[300px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Thời gian</span>
                        <DateRangePicker
                            value={values.time}
                            onChange={(v) => set('time', v)}
                            className="w-full rounded-full"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="md"
                        className="gap-2 rounded-full border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
                        onClick={onSearch}
                    >
                        <Search size={16} /> Tìm kiếm
                    </Button>
                </div>
            </div>
        </FilterFormWrapper>
    )
}